import { DEFAULT_RUBRIC, isLibraryRubric } from '../core/library';
import { isRubric, type Rubric } from '../core/rubric';

/**
 * The rubric lives in the browser. One key, a version field inside the value, and any
 * failure — no storage, a parse error, an older shape — falls back to the default.
 */
export const STORAGE_KEY = 'preview-rubric:rubric:v1';

export function loadRubric(storage: Pick<Storage, 'getItem'> | null = safeStorage()): Rubric {
  if (!storage) return DEFAULT_RUBRIC;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_RUBRIC;
    const parsed: unknown = JSON.parse(raw);
    return isRubric(parsed) && parsed.items.length > 0 && parsed.title !== RETIRED_TITLE ? parsed : DEFAULT_RUBRIC;
  } catch {
    return DEFAULT_RUBRIC;
  }
}

/**
 * The TExES 293 rubric shipped as the default until 2026-10-02 and was saved on every first
 * visit, so a stored copy is almost always the old default rather than a teacher's work.
 * Matched by title; a teacher who kept the title but edited the criteria loses those edits.
 */
const RETIRED_TITLE = 'TExES Science of Teaching Reading (293) - V4';

export function saveRubric(rubric: Rubric, storage: Pick<Storage, 'setItem'> | null = safeStorage()): void {
  writeRubric(STORAGE_KEY, rubric, storage);
}

function writeRubric(key: string, rubric: Rubric, storage: Pick<Storage, 'setItem'> | null): void {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(rubric));
  } catch {
    // Quota or a private window. The rubric still works for this visit.
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/**
 * The teacher's own rubric, kept apart from the one in use so switching to a template and
 * back loses nothing — which is why switching needs no confirmation.
 */
export const MINE_KEY = 'preview-rubric:mine:v1';

export function loadMine(storage: Pick<Storage, 'getItem'> | null = safeStorage()): Rubric | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(MINE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return isRubric(parsed) && parsed.items.length > 0 && !isLibraryRubric(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function saveMine(rubric: Rubric, storage: Pick<Storage, 'setItem'> | null = safeStorage()): void {
  writeRubric(MINE_KEY, rubric, storage);
}
